/* eslint-disable i18n-scan/no-inline-content */
import { Search, AlgoliaSearch } from '@paypalcorp/developersearchui'
import React, { forwardRef, useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Avatar, Col, Row, CoachTip, BodyText } from '@paypalcorp/pp-react'
import { useMessage } from '../../../hooks'
import { getUserType } from '../../../utils/user-utils'
import { NavMenu } from './nav-menu'
import { useApplicationContext } from '../../../context/ApplicationContext'
import { loadUserPreferences } from '../../../helper/user-helper'
import { useNavigate } from 'react-router-dom'
import apiFactory from '../../../utils/api-factory'
import logUtil from '../../../utils/log-util'
import { Message } from '@paypalcorp/worldready-react'
import { recordDynamicEvent } from '../analytics/utils'
import {ACCOUNT_TYPE} from '../../../common/constant'
import { isAlgoliaSearch } from '../../../utils/algoliaUtils'

function getFullName(accountType, currentUser) {
  if (accountType === ACCOUNT_TYPE.IDENTITY) {
    return currentUser?.email || ''
  }
  return [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(' ')
}

function getInitials(accountType, currentUser) {
  if (accountType === ACCOUNT_TYPE.IDENTITY) {
    return currentUser?.email?.trim().toUpperCase().slice(0, 1) || ''
  }
  const first = currentUser?.firstName?.trim().toUpperCase().slice(0, 1) || ''
  const last = currentUser?.lastName?.trim().toUpperCase().slice(0, 1) || ''
  return first + last
}

function getProfileNav(FULL_NAME, maxLength, profileMenuItems) {
  return [
    {
      title:
        FULL_NAME.length > maxLength
          ? FULL_NAME.substr(0, maxLength) + '...'
          : FULL_NAME,
      menuItems: profileMenuItems,
    },
  ]
}

export const LoggedNav = forwardRef(
  ({ currentUser, menuList, profileMenuItems, ...rest }, ref) => {
    const navigate = useNavigate()
    const targetRefTopCenter = useRef()
    const [isMounted, setMounted] = useState(false)
    const [isCommCardClosed, setIsCommCardClosed] = useState(true)
    const { aiSearch, aiSearch_acctID, csrf } = useApplicationContext()
    const COMM_INFOCARD_NAME = 'COMM_INFOCARD'
    const COMM_INFOCARD_VALUE = 'CLOSED'
    const localizedMsg = useMessage()
    const accountType = getUserType(currentUser)
    logUtil.log('accountType info', { accountType })

    const getUserPreferences = async () => {
      try {
        const response = await apiFactory.getNotifications({ csrf })
        const channelStatusOff =
          response && response[0] && response[0].channels?.[0]?.status === 'OFF'
        if (channelStatusOff) {
          const preferences = await loadUserPreferences()
          const commPreference = preferences.find(
            (userPreference) =>
              userPreference.devup_name === COMM_INFOCARD_NAME,
          )
          if (commPreference) {
            setIsCommCardClosed(
              commPreference.devup_value === COMM_INFOCARD_VALUE,
            )
            setMounted(false)
          } else {
            setIsCommCardClosed(false)
            setMounted(true)
          }
        }
      } catch (e) {
        logUtil.error(`error while loading get notification error ${e}`)
      }
    }

    useEffect(() => {
      if (accountType !== 'IDENTITY') {
        getUserPreferences()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const FULL_NAME = getFullName(accountType, currentUser)
    const INITIALS = getInitials(accountType, currentUser)
    const maxLength = 12
    const profileNav = getProfileNav(FULL_NAME, maxLength, profileMenuItems)
    const showAlgoliaSearch = isAlgoliaSearch(aiSearch, aiSearch_acctID)

    const handlerMyAccount = async () => {
      recordDynamicEvent(`notification_tooltip,click,tooltip_myaccount_clicked`)
      const userNotificationPreference = {
        preferenceName: COMM_INFOCARD_NAME,
        preferenceValue: COMM_INFOCARD_VALUE,
      }
      await apiFactory.addUserPreference(csrf, userNotificationPreference)
      setIsCommCardClosed(true)
      navigate('/accountStatus?source=notification')
      setMounted(false)
    }

    const tipContent = (
      <span className="">
        Open{' '}
        <BodyText
          style={{
            color: '#0070E0',
            cursor: 'pointer',
            display: 'inline-block',
          }}
          onClick={handlerMyAccount}
          target={'_self'}
        >
          <Message id="account-notifications.toltip.myaccount" />
        </BodyText>{' '}
        <Message id="account-notifications.toltip.content" />
      </span>
    )

    const toltipCloseHandler = () => {
      recordDynamicEvent(`notification_tooltip,close,tooltip_closeIcon_clicked`)
      setMounted(false)
    }

    return (
      <nav className="dev_dash__logged_nav--container" ref={ref} {...rest}>
        <Row className="flex items-center top-nav-items-center">
          <Col xs={4} className="dev_dash__logged_nav--container-flex">
            <div className="dev_dash__logged_nav--LoggedNav-title">
              {localizedMsg('top-nav.logged.title')}
            </div>
            <div id="dev_dash__logged_nav--search-id">
              {!showAlgoliaSearch ? (
                <Search
                  isSecondary={false}
                  searchBtnStyles={'logged__nav--search-button'}
                  component={'devdashnodeweb'}
                  clientFptiProps={{
                    space_key: 'SKEXJE',
                  }}
                />
              ) : (
                <AlgoliaSearch
                  buttonStyle={{
                    isSecondary: true,
                    isInverse: true,
                    size: 'sm',
                  }}
                  fptiOptions={{
                    space_key: 'SKEXJE',
                    component: 'devdashnodeweb',
                  }}
                />
              )}
            </div>
          </Col>
          <Col xs={8}>
            <Row className="flex items-center flex-end">
              <NavMenu menuList={menuList} />
              <div className="dev_dash__logged_nav-item_link__border" />
              <div className="flex items-center ml-10">
                <div
                  ref={targetRefTopCenter}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setMounted(true)}
                >
                  <Avatar initials={INITIALS} size="xs" />
                </div>
                {!isCommCardClosed &&
                  isMounted &&
                  accountType !== 'IDENTITY' && (
                    <CoachTip
                      caretPos="top-center"
                      text={tipContent}
                      targetElementRef={targetRefTopCenter}
                      isOpen
                      onClose={toltipCloseHandler}
                      style={{ position: 'fixed' }}
                    />
                  )}
                <NavMenu menuList={profileNav} />
              </div>
            </Row>
          </Col>
        </Row>
      </nav>
    )
  },
)

LoggedNav.displayName = 'LoggedNav'

LoggedNav.propTypes = {
  currentUser: PropTypes.object,
  profileMenuItems: PropTypes.array,
  menuList: PropTypes.array,
}
